/**
 * "What do I usually charge for a powerflush?" — the price-list figure beside
 * what the owner has actually quoted for it, as a strip of dots
 * (docs/intelligent-chat-answers-plan.md, "Price range").
 *
 * Built by the server from two reads it made itself — the price list and the
 * lines of the account's sent quotes — never from anything the model wrote.
 * Every figure is EX VAT: `price_list_items.sale_price` and
 * `quote_line_items.unit_price` are both stored ex VAT, and a quote's VAT is
 * added once, on the quote, from `quotes.tax_rate`.
 *
 * 🚨 The footer names WHERE it looked and nothing more (#31). It never says
 * there is nowhere else to look — invoice and quote templates exist, and the
 * price-list tool searches those separately — and an empty search ends by
 * asking what the item is called on the owner's screen, never "not on file".
 *
 * Pure and directive-free (anti-pattern #18): the route, the panel, the Mucka
 * Bar and the oversight grader all import it.
 */
import { londonTodayIso } from '@/lib/on-time/local-time';
import { ukDate } from '@/lib/utils/date-format';
import { rankByQuery, tokenise } from '@/lib/price-list/match';

/** A quote that went to a customer. Drafts and cancelled quotes never did. */
export const PRICE_RANGE_QUOTE_STATUSES = ['sent', 'accepted', 'declined', 'expired'] as const;
export type PriceRangeQuoteStatus = typeof PRICE_RANGE_QUOTE_STATUSES[number];

/** How far back the strip looks. Older prices are history, not "usually". */
export const PRICE_RANGE_WINDOW_MONTHS = 12;
/** Dots drawn. The rest are counted on the block, never dropped silently. */
export const PRICE_RANGE_MAX_DOTS = 20;
/**
 * A query longer than this is a shopping list ("labour hourly rate WC fill
 * valve dump valve closed coupling kit"), not "what do I charge for X", and a
 * strip about all of it would be about none of it.
 */
export const PRICE_RANGE_MAX_TOKENS = 5;

/** One quoted price: a line on a quote that went to a customer. */
export interface PriceRangeDot {
  /** `quote_line_items.id`. */
  lineId: string;
  quoteId: string;
  /** "Q-1019", or "Quote" when the quote has no reference. */
  reference: string;
  /** London calendar day the quote went out (sent, else created). YYYY-MM-DD. */
  date: string;
  /** "22 Sep 2026", built here so every surface says the same thing. */
  dateLabel: string;
  /** The line as the owner wrote it, verbatim. */
  description: string;
  /** Ex VAT, pounds, per unit, as stored. */
  unitPrice: number;
  quantity: number;
  status: PriceRangeQuoteStatus;
  /** Where the row opens. */
  path: string;
}

export interface PriceRangeListItem {
  name: string;
  /** Ex VAT, pounds. */
  price: number;
  /** "each", "hour", "metre" — as stored. */
  unit: string | null;
}

export interface PriceRangeBlock {
  kind: 'price-range';
  /** What was searched for, as the tool was asked. */
  query: string;
  /** The best price-list item that matched every word, or null. */
  listItem: PriceRangeListItem | null;
  /** Further price-list items that also matched every word, not marked. */
  otherListMatches: number;
  /** Newest first, at most PRICE_RANGE_MAX_DOTS. */
  dots: PriceRangeDot[];
  /** Matching quote lines in the window, INCLUDING any not drawn. */
  totalInWindow: number;
  windowMonths: number;
}

/** A price-list row as the reader hands it over. */
export interface PriceListCandidate {
  name: string;
  description: string | null;
  category: string | null;
  sku: string | null;
  unit: string | null;
  salePrice: number;
  /** Priced by time (call-out + first period): `salePrice` is then the rate
   *  AFTER the first period, so marking it would mislead. */
  timePriced: boolean;
}

/** A quote line as the reader hands it over. */
export interface QuoteLineCandidate {
  lineId: string;
  quoteId: string;
  reference: string | null;
  status: string;
  /** ISO timestamp the quote went out: `sent_at`, else `created_at`. */
  at: string;
  description: string;
  unitPrice: number;
  quantity: number;
}

/**
 * The same haystack `executeLookupPriceList` matches on, so the marker is the
 * item the tool itself would name first.
 */
export function priceListHaystack(r: Pick<PriceListCandidate, 'name' | 'description' | 'category' | 'sku'>): string {
  return `${r.name} ${r.description ?? ''} ${r.category ?? ''} ${r.sku ?? ''}`;
}

/** True when a query is the kind of question this strip answers. */
export function isPriceRangeQuery(query: string): boolean {
  const tokens = tokenise(query);
  return tokens.length > 0 && tokens.length <= PRICE_RANGE_MAX_TOKENS;
}

/**
 * The SQL pre-filter for quote lines: one `ilike` per token on the token's
 * first four characters. It is a SUPERSET of what `scoreHaystack` counts as a
 * hit for that token — a substring hit contains them, and a prefix hit either
 * way needs both words at least four long, so the shorter one's first four are
 * the longer one's too — so ANDing them narrows the read without losing a line
 * the JS match below would keep. Tokens are `[a-z0-9]` only, so nothing here
 * can carry a `%`, `_` or a comma into the filter (#4).
 */
export function quoteLinePrefilters(query: string): string[] {
  return tokenise(query).map(t => `%${t.slice(0, 4)}%`);
}

const WHOLE = 1;

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function yearBefore(todayIso: string, months: number): string {
  const [y, m, d] = todayIso.split('-').map(Number);
  const back = new Date(Date.UTC(y, m - 1 - months, d));
  return back.toISOString().slice(0, 10);
}

function isQuoteStatus(s: string): s is PriceRangeQuoteStatus {
  return (PRICE_RANGE_QUOTE_STATUSES as readonly string[]).includes(s);
}

/**
 * The block, or null when the question is not one this answers.
 *
 * A line or item counts only when it accounts for EVERY word of the query
 * (score ≥ 1 from `rankByQuery`). The price-list tool keeps partial matches
 * because a list of names is something the owner can pick from; a range of
 * prices is not — "boiler service" half-matching "boiler installation" would
 * put a £2,000 dot on a £90 question.
 *
 * `todayIso` is London's date, passed in so this stays pure.
 */
export function priceRangeBlock(
  query: string,
  listRows: readonly PriceListCandidate[],
  quoteLines: readonly QuoteLineCandidate[],
  todayIso: string,
): PriceRangeBlock | null {
  const q = query.trim();
  if (!isPriceRangeQuery(q)) return null;

  const listMatches = rankByQuery(listRows.filter(r => !r.timePriced && r.salePrice > 0), q, priceListHaystack)
    .filter(m => m.score >= WHOLE);
  const top = listMatches[0]?.row ?? null;

  const since = yearBefore(todayIso, PRICE_RANGE_WINDOW_MONTHS);
  const inWindow = rankByQuery(
    // A £0 line ("included", "no charge") is not something anybody charged.
    quoteLines.filter(l => isQuoteStatus(l.status) && l.unitPrice > 0 && l.description.trim()),
    q,
    l => l.description,
  )
    .filter(m => m.score >= WHOLE)
    .map(m => m.row)
    .map(l => ({ line: l, date: londonTodayIso(new Date(l.at)) }))
    .filter(x => x.date >= since && x.date <= todayIso)
    .sort((a, b) => b.date.localeCompare(a.date) || b.line.at.localeCompare(a.line.at) || a.line.lineId.localeCompare(b.line.lineId));

  const dots: PriceRangeDot[] = inWindow.slice(0, PRICE_RANGE_MAX_DOTS).map(({ line, date }) => ({
    lineId: line.lineId,
    quoteId: line.quoteId,
    reference: line.reference?.trim() || 'Quote',
    date,
    dateLabel: ukDate(new Date(line.at), { day: 'numeric', month: 'short', year: 'numeric' }),
    description: line.description.trim(),
    unitPrice: round2(line.unitPrice),
    quantity: line.quantity,
    status: line.status as PriceRangeQuoteStatus,
    path: `/quotes?open=${line.quoteId}`,
  }));

  return {
    kind: 'price-range',
    query: q,
    listItem: top ? { name: top.name, price: round2(top.salePrice), unit: top.unit } : null,
    otherListMatches: Math.max(0, listMatches.length - 1),
    dots,
    totalInWindow: inWindow.length,
    windowMonths: PRICE_RANGE_WINDOW_MONTHS,
  };
}

/** Lowest and highest DRAWN price, or null with no dots. */
export function drawnRange(block: Pick<PriceRangeBlock, 'dots'>): { low: number; high: number } | null {
  if (block.dots.length === 0) return null;
  const prices = block.dots.map(d => d.unitPrice);
  return { low: Math.min(...prices), high: Math.max(...prices) };
}

/**
 * The single price quoted more often than any other among the drawn dots, when
 * one was quoted at least twice. Null otherwise: two prices tied, or every
 * price different, has no "most often" and none is invented.
 */
export function mostOftenPrice(block: Pick<PriceRangeBlock, 'dots'>): number | null {
  const counts = new Map<number, number>();
  for (const d of block.dots) counts.set(d.unitPrice, (counts.get(d.unitPrice) ?? 0) + 1);
  let best: number | null = null;
  let bestCount = 1;
  let tied = false;
  for (const [price, n] of counts) {
    if (n > bestCount) { best = price; bestCount = n; tied = false; }
    else if (n === bestCount && best !== null) tied = true;
  }
  return tied ? null : best;
}

export interface StripScale {
  /** Axis ends, in pounds, rounded outward to a tidy figure. */
  min: number;
  max: number;
}

/** A tidy step for an axis spanning `span` pounds: 5, 10, 20, 50, 100… */
function niceStep(span: number): number {
  const raw = span / 4;
  const mag = 10 ** Math.floor(Math.log10(raw));
  for (const f of [1, 2, 5, 10]) if (f * mag >= raw) return f * mag;
  return 10 * mag;
}

/** The axis the strip is drawn on: every dot and the list marker, with room either side. */
export function stripScale(block: Pick<PriceRangeBlock, 'dots' | 'listItem'>): StripScale | null {
  const prices = block.dots.map(d => d.unitPrice);
  if (block.listItem) prices.push(block.listItem.price);
  if (prices.length === 0) return null;
  const lo = Math.min(...prices);
  const hi = Math.max(...prices);
  const span = hi - lo || Math.max(hi * 0.2, 10);
  const step = niceStep(span);
  const min = Math.max(0, Math.floor((lo - span * 0.15) / step) * step);
  const max = Math.ceil((hi + span * 0.15) / step) * step;
  return { min, max: max > min ? max : min + step };
}

/** Where a price sits on the strip, 0–100. */
export function stripX(price: number, scale: StripScale): number {
  return Math.min(100, Math.max(0, ((price - scale.min) / (scale.max - scale.min)) * 100));
}

/**
 * Each dot's place on the strip. Dots at the same price stack upward instead
 * of hiding behind one another, so four quotes at £420 read as four.
 */
export function dotPositions(block: Pick<PriceRangeBlock, 'dots' | 'listItem'>): { lineId: string; x: number; stack: number }[] {
  const scale = stripScale(block);
  if (!scale) return [];
  const seen = new Map<number, number>();
  // Oldest first, so the newest quote at a price sits on top of its stack.
  return [...block.dots].reverse().map(d => {
    const stack = seen.get(d.unitPrice) ?? 0;
    seen.set(d.unitPrice, stack + 1);
    return { lineId: d.lineId, x: stripX(d.unitPrice, scale), stack };
  }).reverse();
}

export function pounds(n: number): string {
  return Number.isInteger(n)
    ? `£${n.toLocaleString('en-GB')}`
    : `£${n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** "1 quoted price" / "6 quoted prices" / "the 20 most recent of 34 quoted prices". */
export function countPhrase(block: Pick<PriceRangeBlock, 'dots' | 'totalInWindow'>): string {
  const shown = block.dots.length;
  const total = block.totalInWindow;
  if (shown < total) return `the ${shown} most recent of ${total} quoted prices`;
  return total === 1 ? '1 quoted price' : `${total} quoted prices`;
}

/**
 * Where it looked, said plainly. Never how much is left (#31): templates are
 * searched by the price-list tool itself, and this footer does not speak for
 * them either way.
 */
export function priceRangeFooter(block: Pick<PriceRangeBlock, 'windowMonths'>): string {
  return `Looked in your price list and your sent quotes from the last ${block.windowMonths} months. Ex VAT throughout.`;
}

/** The empty state's words: what it searched, and a question rather than a verdict. */
export function priceRangeEmptyText(block: Pick<PriceRangeBlock, 'query' | 'windowMonths'>): string {
  return `Nothing on your price list or in your sent quotes from the last ${block.windowMonths} months matched "${block.query}". `
    + 'If it is on file under another name, tell me what it is called on your screen and I will look for that.';
}

/**
 * The sentence appended to `lookup_price_list`'s RESULT so the reply can carry
 * the answer on its own (the block is extra): the quoted range, in words the
 * model can repeat, plus the instruction not to read the strip out.
 *
 * The price-list tool never read past quote lines before, so on a miss this is
 * a FOURTH place — it must not contradict "nothing found in the price list",
 * which stays true, and it must not imply the search is now complete.
 */
export function priceRangeResultNote(block: PriceRangeBlock): string {
  const range = drawnRange(block);
  const where = `on their sent quotes from the last ${block.windowMonths} months`;
  if (!range) {
    return `\n\nI also looked at the lines ${where}: none matched "${block.query}". `
      + 'That is on screen. It may still be on file under other wording: if they say it is there, ask what it is called on their screen and search that.';
  }
  let said: string;
  if (block.totalInWindow === 1) {
    said = `one line ${where} matched "${block.query}", at ${pounds(range.low)} ex VAT per unit`;
  } else {
    const often = mostOftenPrice(block);
    const spread = range.low === range.high
      ? `every one at ${pounds(range.low)}`
      : `between ${pounds(range.low)} and ${pounds(range.high)}${often !== null ? `, most often ${pounds(often)}` : ''}`;
    const counted = block.dots.length < block.totalInWindow
      ? `${block.totalInWindow} lines ${where} matched "${block.query}"; the ${block.dots.length} most recent were`
      : `${block.totalInWindow} lines ${where} matched "${block.query}",`;
    said = `${counted} ${spread} ex VAT per unit`;
  }
  const beside = block.listItem ? ' beside the price-list figure' : '';
  return `\n\nPast quotes: ${said}. These are on screen as a price range${beside}, with each quote listed. `
    + 'Say the range in one sentence; do not read the quotes out one by one. '
    + 'A past quote is not a set price: never put one of these figures on a new quote or invoice unless they say to.';
}

/** One line for the oversight grader: counts and figures, nobody's name. */
export function summarisePriceRangeBlock(block: PriceRangeBlock): string {
  const range = drawnRange(block);
  const list = block.listItem ? `list ${pounds(block.listItem.price)}` : 'no list match';
  if (!range) return `[price-range: ${list}, no matching quoted prices in ${block.windowMonths} months, searched price list and sent quotes]`;
  const span = range.low === range.high ? pounds(range.low) : `${pounds(range.low)}–${pounds(range.high)}`;
  const drawn = block.dots.length < block.totalInWindow ? `${block.dots.length} of ${block.totalInWindow}` : `${block.totalInWindow}`;
  return `[price-range: ${list}, ${drawn} quoted price${block.totalInWindow === 1 ? '' : 's'} ${span} ex VAT in ${block.windowMonths} months]`;
}

/** Which statuses read as what, on a row. */
export const PRICE_RANGE_STATUS_LABEL: Record<PriceRangeQuoteStatus, string> = {
  sent: 'Sent',
  accepted: 'Accepted',
  declined: 'Declined',
  expired: 'Expired',
};
